import { alpha, ButtonBase, Grid, IconButton, Typography } from '@mui/material';
import { Add as AddIcon, ChevronLeft, ChevronRight, Delete } from '@mui/icons-material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { UploadWidgetResult } from '@bytescale/upload-widget';
import { OrderPageImageData } from '../../../api';
import { ImageUploadWidget } from '../../../emailTemplates/templateEditor/components/bytescale/ImageUploadWidget';
import { optimizeImageUrl } from '../../../utils/orderPageImageUtils';

type OrderPageImagesPanelProps = {
  images: OrderPageImageData[];
  onChange: (images: OrderPageImageData[]) => void;
  disabled?: boolean;
};

export const OrderPageImagesPanel = ({ images, onChange, disabled }: OrderPageImagesPanelProps) => {
  const { t, i18n } = useTranslation();

  const sortedImages = [...images].sort((a, b) => a.order - b.order);

  const getNextOrder = (): number => {
    if (images.length === 0) return 0;
    return Math.max(...images.map((img) => img.order)) + 1;
  };

  const handleImageUpload = (files: UploadWidgetResult[]) => {
    if (files.length === 0) return;
    const startOrder = getNextOrder();
    const newImages = files.map((file, index) => ({
      value: file.fileUrl,
      type: 'image' as const,
      order: startOrder + index,
    }));
    onChange([...images, ...newImages]);
  };

  const handleDelete = (index: number) => {
    const imageToDelete = sortedImages[index];
    onChange(images.filter((img) => img !== imageToDelete));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newImages = [...sortedImages];
    // Swap order values
    const currentOrder = newImages[index].order;
    const prevOrder = newImages[index - 1].order;
    newImages[index] = { ...newImages[index], order: prevOrder };
    newImages[index - 1] = { ...newImages[index - 1], order: currentOrder };
    onChange(newImages);
  };

  const handleMoveDown = (index: number) => {
    if (index === sortedImages.length - 1) return;
    const newImages = [...sortedImages];
    // Swap order values
    const currentOrder = newImages[index].order;
    const nextOrder = newImages[index + 1].order;
    newImages[index] = { ...newImages[index], order: nextOrder };
    newImages[index + 1] = { ...newImages[index + 1], order: currentOrder };
    onChange(newImages);
  };

  const getThumbnailUrl = (image: OrderPageImageData): string => {
    return optimizeImageUrl(image.value, 120) || '';
  };

  const isRtl = i18n.dir() === 'rtl';
  const thumbnailWidth = { xs: 120, sm: 168 };
  const thumbnailHeight = { xs: 91, sm: 128 };

  return (
    <Grid container flexDirection="column" gap={1.25}>
      {sortedImages.length === 0 && (
        <Typography color="text.secondary" variant="body2">
          {t('editOrderPage.noGalleryImages')}
        </Typography>
      )}

      <Grid container gap="14px" flexWrap="wrap">
        {sortedImages.map((image, index) => (
          <Grid key={index} container flexDirection="column" alignItems="center" gap={1} sx={{ width: thumbnailWidth }}>
            <Grid
              container
              alignItems="center"
              justifyContent="center"
              sx={{
                position: 'relative',
                width: thumbnailWidth,
                height: thumbnailHeight,
                borderRadius: '6px',
                bgcolor: 'mb.side',
                overflow: 'hidden',
              }}>
              <img
                src={getThumbnailUrl(image)}
                alt={`Gallery ${index + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <IconButton
                onClick={() => handleDelete(index)}
                disabled={disabled}
                aria-label={t('editOrderPage.deleteImage')}
                sx={{
                  position: 'absolute',
                  top: 8,
                  insetInlineStart: 8,
                  width: 34,
                  height: 34,
                  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.94),
                  color: 'error.main',
                  '&:hover': { bgcolor: 'background.paper' },
                }}>
                <Delete fontSize="small" />
              </IconButton>
              {index === 0 && (
                <Typography
                  component="span"
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    insetInlineStart: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    px: 1.25,
                    py: 0.375,
                    borderRadius: '4px',
                    bgcolor: 'primary.light',
                    color: 'primary.main',
                  }}>
                  {t('editOrderPage.coverImage')}
                </Typography>
              )}
            </Grid>
            <Grid container justifyContent="center" gap={0.5}>
              <IconButton
                onClick={() => handleMoveUp(index)}
                disabled={disabled || index === 0}
                aria-label={t('editOrderPage.moveImageEarlier')}
                sx={{ width: 36, height: 36 }}>
                {isRtl ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
              </IconButton>
              <IconButton
                onClick={() => handleMoveDown(index)}
                disabled={disabled || index === sortedImages.length - 1}
                aria-label={t('editOrderPage.moveImageLater')}
                sx={{ width: 36, height: 36 }}>
                {isRtl ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <ImageUploadWidget
          onImageUpload={handleImageUpload}
          maxFileCount={6}
          renderTrigger={(onClick) => (
            <ButtonBase
              onClick={onClick}
              sx={{
                width: thumbnailWidth,
                height: thumbnailHeight,
                border: '1.5px dashed',
                borderColor: 'mb.control',
                borderRadius: '6px',
                color: 'primary.main',
                fontSize: 14,
                fontWeight: 500,
                flexDirection: 'column',
                gap: 0.75,
              }}>
              <AddIcon sx={{ fontSize: 22 }} />
              {t('editOrderPage.addImage')}
            </ButtonBase>
          )}
        />
      </Grid>
    </Grid>
  );
};
